import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Download, Eye, Lock, Mail, RotateCcw, Send } from 'lucide-react';
import AdminShell from '../../components/Survey/admin/AdminShell';
import EmployeesTab from '../../components/Survey/admin/tabs/EmployeesTab';
import DashboardTab from '../../components/Survey/admin/tabs/DashboardTab';
import ResultsTab from '../../components/Survey/admin/tabs/ResultsTab';
import ConsultantTab from '../../components/Survey/admin/tabs/ConsultantTab';
import { parseInvitationCsvText } from '../../components/Survey/admin/surveyInvitationCsv';
import { prettyDate, statusVariant } from '../../components/Survey/admin/surveyUiUtils';
import { Actions as SURVEY_ACTIONS } from '../../store/actions/surveyActions';
import { Actions as FACILITY_ACTIONS } from '../../store/actions/facilityActions';

const SurveyDetail = ({
  campaign,
  report,
  responses,
  facilityList,
  facilityMetrics,
  currentOrganisation,
  loading,
  getCampaign,
  setCampaignFacilities,
  addInvitations,
  deleteInvitation,
  remindInvitation,
  sendInvitations,
  remindNonRespondents,
  closeCampaign,
  reopenCampaign,
  getCampaignReport,
  getCampaignResults,
  getCampaignResponses,
  exportCampaignResponsesCsv,
  flagResponse,
  getFacilities,
  getFacilityMetrics,
}) => {
  const { id } = useParams();
  const [facilities, setFacilities] = useState([]);
  const [invitationInput, setInvitationInput] = useState('');

  useEffect(() => {
    getCampaign(id);
    getCampaignReport(id);
    getCampaignResults(id);
    getCampaignResponses(id);
  }, [id, getCampaign, getCampaignReport, getCampaignResults, getCampaignResponses]);

  useEffect(() => {
    if (!currentOrganisation) return;
    getFacilities();
    getFacilityMetrics();
  }, [currentOrganisation, getFacilities, getFacilityMetrics]);

  useEffect(() => {
    if (!campaign) return;
    const byId = new Map((facilityList || []).map((facility) => [String(facility.id), facility]));
    const fteOverrides = campaign?.fteOverrides && typeof campaign.fteOverrides === 'object' ? campaign.fteOverrides : {};

    if (Array.isArray(campaign?.facilities) && campaign.facilities.length > 0) {
      setFacilities(
        campaign.facilities.map((facility) => ({
          id: facility.id || facility.facilityId || null,
          name: facility.name || facility.facilityName || byId.get(String(facility.id || facility.facilityId || ''))?.name || '',
          fte:
            Number(fteOverrides[facility.id || facility.facilityId]) ||
            Number(facility.fte || facility.fteCount || facility.metric2 || 0),
        })),
      );
      return;
    }

    if (Array.isArray(campaign?.facilityIds) && campaign.facilityIds.length > 0) {
      setFacilities(
        campaign.facilityIds.map((facilityId) => {
          const facility = byId.get(String(facilityId));
          return {
            id: facilityId,
            name: facility?.name || facilityId,
            fte: Number(fteOverrides[facilityId] || 0),
          };
        }),
      );
      return;
    }

    if (Array.isArray(campaign?.facilityNames) && campaign.facilityNames.length > 0) {
      setFacilities(campaign.facilityNames.map((name) => ({ id: null, name, fte: 0 })));
      return;
    }

    setFacilities([]);
  }, [campaign, facilityList]);

  const invitationRows = useMemo(() => {
    const facilityById = new Map((facilityList || []).map((f) => [String(f.id), f]));

    const asInvitationArray = (raw) => {
      if (Array.isArray(raw)) return raw;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) return Object.values(raw);
      return [];
    };

    const normalizeInvitation = (inv) => {
      const fid = inv?.facilityId ?? inv?.facility_id;
      const fromFacility = fid != null && String(fid) !== '' ? facilityById.get(String(fid))?.name : null;
      return {
        ...inv,
        id: inv.id || inv.invitationId,
        invitationId: inv.id || inv.invitationId || null,
        employeeName: inv.employeeName || inv.name,
        employeeEmail: inv.employeeEmail || inv.email,
        email: inv.email || inv.employeeEmail,
        locationName:
          inv.locationName ||
          inv.location ||
          inv.facilityName ||
          inv.officeLocation ||
          fromFacility ||
          null,
        sentAt: inv.sentAt || inv.invitedAt,
        completedAt: inv.completedAt || inv.submittedAt,
      };
    };

    const fromInvitations = asInvitationArray(campaign?.invitations);
    if (fromInvitations.length > 0) return fromInvitations.map(normalizeInvitation);

    const fromEmployees = asInvitationArray(campaign?.employees);
    if (fromEmployees.length > 0) return fromEmployees.map(normalizeInvitation);

    if (Array.isArray(responses) && responses.length > 0) {
      return responses.map((responseItem) => ({
        id: responseItem.id || responseItem.responseId,
        invitationId: null,
        employeeName: responseItem.employeeName,
        employeeEmail: responseItem.employeeEmail,
        email: responseItem.employeeEmail,
        locationName: responseItem.facilityName,
        status: responseItem.status,
        sentAt: responseItem.sentAt || responseItem.invitedAt,
        completedAt: responseItem.completedAt || responseItem.submittedAt,
      }));
    }
    return [];
  }, [campaign, responses, facilityList]);

  const getFacilityFteForCampaignPeriod = (facilityId) => {
    const start = campaign?.reportingPeriodStart;
    const end = campaign?.reportingPeriodEnd;
    if (!facilityId || !start || !end) return 0;

    const metricsForFacility = (facilityMetrics || []).filter((metric) => String(metric.facility || metric.facilityId) === String(facilityId));
    if (!metricsForFacility.length) return 0;

    const exact = metricsForFacility.find((metric) => metric.startDate === start && metric.endDate === end);
    if (exact) return Number(exact.metric2 || exact.fte || 0);

    const inRange = metricsForFacility.find((metric) => {
      if (!metric.startDate || !metric.endDate) return false;
      return metric.startDate <= start && metric.endDate >= end;
    });

    return Number(inRange?.metric2 || inRange?.fte || 0);
  };

  const addFacilityById = (facilityId) => {
    if (!facilityId) return;
    const exists = facilities.some((facility) => String(facility.id) === String(facilityId));
    if (exists) return;

    const facility = (facilityList || []).find((item) => String(item.id) === String(facilityId));
    const defaultFte = getFacilityFteForCampaignPeriod(facilityId);

    setFacilities([
      ...facilities,
      {
        id: facilityId,
        name: facility?.name || facilityId,
        fte: defaultFte,
      },
    ]);
  };

  const submitFacilities = async (nextFacilities, draftOnly = false) => {
    if (draftOnly) {
      setFacilities(nextFacilities);
      return;
    }

    const cleanedFacilities = nextFacilities
      .map((facility) => ({
        id: facility.id || null,
        name: String(facility.name || '').trim(),
        fte: Number(facility.fte || 0),
      }))
      .filter((facility) => facility.id && facility.name);

    const facilityIds = cleanedFacilities.map((facility) => facility.id);
    const fteOverrides = cleanedFacilities.reduce((acc, facility) => {
      acc[facility.id] = Number(facility.fte || 0);
      return acc;
    }, {});

    await setCampaignFacilities(id, { facilityIds, fteOverrides });
  };

  const submitInvitations = async () => {
    const { invitations } = parseInvitationCsvText(invitationInput);
    if (!invitations.length) return;
    await addInvitations(id, { invitations });
    setInvitationInput('');
  };

  const bulkImportInvitations = async (invitations) => {
    const data = await addInvitations(id, { invitations });
    return data != null;
  };

  const canClose = (campaign?.status || '').toLowerCase() !== 'closed';
  const campaignStatus = String(campaign?.status || 'draft').toLowerCase();
  const reportingPeriodLabel =
    campaign?.reportingPeriod ||
    (campaign?.reportingPeriodStart && campaign?.reportingPeriodEnd
      ? `${campaign.reportingPeriodStart} to ${campaign.reportingPeriodEnd}`
      : campaign?.reportingPeriodStart || 'Reporting period');

  return (
    <AdminShell
      breadcrumbs={[
        { label: 'Surveys', to: '/surveys' },
        { label: campaign?.name || 'Campaign' },
      ]}
      eyebrow={`${reportingPeriodLabel} campaign`}
      title={campaign?.name || 'Campaign'}
      description={
        <span className='inline-flex items-center gap-3 flex-wrap'>
          <Badge variant={statusVariant(campaign?.status)} size='default'>
            {campaign?.statusLabel || campaign?.status || 'draft'}
          </Badge>
          <span className='text-sm text-muted-foreground'>Deadline {prettyDate(campaign?.deadlineDate)}</span>
          {campaignStatus === 'closed' && campaign?.closedAt ? (
            <span className='text-sm text-muted-foreground'>· Closed {prettyDate(campaign.closedAt)}</span>
          ) : null}
        </span>
      }
      actions={
        <>
          <Button variant='ghost' asChild>
            <Link to='/surveys'>
              <ArrowLeft size={14} strokeWidth={1.5} />
              All campaigns
            </Link>
          </Button>
          {campaignStatus === 'closed' ? (
            <>
              <Button variant='outline' onClick={() => reopenCampaign(id)} loading={Boolean(loading?.reopen)}>
                <RotateCcw size={14} strokeWidth={1.5} />
                Re-open
              </Button>
              <Button
                variant='outline'
                onClick={() => exportCampaignResponsesCsv(id, campaign?.name)}
                loading={Boolean(loading?.exportCampaign)}>
                <Download size={14} strokeWidth={1.5} />
                Export
              </Button>
            </>
          ) : (
            <>
              <Button variant='outline' onClick={() => sendInvitations(id)} loading={Boolean(loading?.send)}>
                <Send size={14} strokeWidth={1.5} />
                Send invitations
              </Button>
              <Button variant='outline' onClick={() => remindNonRespondents(id)} loading={Boolean(loading?.remind)}>
                <Mail size={14} strokeWidth={1.5} />
                Send reminders
              </Button>
              <Button onClick={() => (canClose ? closeCampaign(id) : reopenCampaign(id))} loading={Boolean(loading?.close || loading?.reopen)}>
                <Lock size={14} strokeWidth={1.5} />
                {canClose ? 'Close campaign' : 'Re-open'}
              </Button>
            </>
          )}
        </>
      }>
      <Tabs defaultValue='employees'>
        <TabsList>
          <TabsTrigger value='employees'>Employees</TabsTrigger>
          <TabsTrigger value='dashboard'>Dashboard</TabsTrigger>
          <TabsTrigger value='results'>Results</TabsTrigger>
          <TabsTrigger value='consultant'>
            <Eye size={14} strokeWidth={1.5} />
            Consultant
          </TabsTrigger>
        </TabsList>

        <TabsContent value='employees'>
          <EmployeesTab
            campaign={campaign || {}}
            facilities={facilities}
            onSaveFacilities={submitFacilities}
            onAddFacility={addFacilityById}
            facilityOptions={(facilityList || []).map((facility) => ({ value: facility.id, label: facility.name }))}
            invitationRows={invitationRows}
            invitationDraft={invitationInput}
            onInvitationDraftChange={setInvitationInput}
            onSaveInvitations={submitInvitations}
            onBulkImportInvitations={bulkImportInvitations}
            onDeleteInvitation={(invitationId) => deleteInvitation(id, invitationId)}
            onResendInvitation={(invitationId) => remindInvitation(id, invitationId)}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value='dashboard'>
          <DashboardTab
            campaign={campaign || {}}
            invitationRows={invitationRows}
            facilities={facilities}
            onRemindClick={() => remindNonRespondents(id)}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value='results'>
          <ResultsTab
            campaign={campaign || {}}
            report={report}
            responses={responses}
            facilities={facilities}
            onExportAll={() => exportCampaignResponsesCsv(id, campaign?.name)}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value='consultant'>
          <ConsultantTab
            campaign={campaign || {}}
            responses={responses}
            facilityList={facilityList}
            report={report}
            onFlagResponse={(responseId, payload) => flagResponse(id, responseId, payload)}
            loading={loading}
          />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
};

const mapStateToProps = (state) => ({
  campaign: state.survey?.currentCampaign,
  report: state.survey?.campaignReport,
  responses: state.survey?.campaignResponses || [],
  facilityList: state.facility?.list || [],
  facilityMetrics: state.facility?.metrics || [],
  currentOrganisation: state.currentOrganisation,
  loading: state.survey?.loading || {},
});

const mapDispatchToProps = {
  getCampaign: SURVEY_ACTIONS.getCampaign,
  setCampaignFacilities: SURVEY_ACTIONS.setCampaignFacilities,
  addInvitations: SURVEY_ACTIONS.addInvitations,
  deleteInvitation: SURVEY_ACTIONS.deleteInvitation,
  remindInvitation: SURVEY_ACTIONS.remindInvitation,
  sendInvitations: SURVEY_ACTIONS.sendInvitations,
  remindNonRespondents: SURVEY_ACTIONS.remindNonRespondents,
  closeCampaign: SURVEY_ACTIONS.closeCampaign,
  reopenCampaign: SURVEY_ACTIONS.reopenCampaign,
  getCampaignReport: SURVEY_ACTIONS.getCampaignReport,
  getCampaignResults: SURVEY_ACTIONS.getCampaignResults,
  getCampaignResponses: SURVEY_ACTIONS.getCampaignResponses,
  exportCampaignResponsesCsv: SURVEY_ACTIONS.exportCampaignResponsesCsv,
  flagResponse: SURVEY_ACTIONS.flagResponse,
  getFacilities: FACILITY_ACTIONS.getFacilities,
  getFacilityMetrics: FACILITY_ACTIONS.getFacilityMetrics,
};

export default connect(mapStateToProps, mapDispatchToProps)(SurveyDetail);
